import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import { komaIndexToTime } from '~/models/komaToTime'
import type { DetailFilterState } from '~/pages/BalanceDemandDetail/constants'
import { GC_KOMA } from '~/pages/BalanceDemandDetail/constants'

export type ActualStatus = 'actual' | 'missing' | 'interpolated' | 'future'

const ACTUAL_STATUSES: ReadonlySet<string> = new Set<ActualStatus>([
  'actual',
  'missing',
  'interpolated',
  'future',
])

export function toActualStatus(v: string): ActualStatus {
  return ACTUAL_STATUSES.has(v) ? (v as ActualStatus) : 'future'
}

export type DetailKomaData = {
  koma: number
  time: string
  /** 計画なしは null。0 で埋めない (計画 0 と区別する)。 */
  demandPlan: number | null
  demandActual: number | null
  actualStatus: ActualStatus
  demandActualCorrected: number | null
  temperature: number | null
  temperatureForecast: number | null
  /** 実績露点 (℃)。backend が nil を返す間 (#2002) は null (0℃ で埋めない)。 */
  dewPoint: number | null
  dewPointForecast: number | null
  precipitation: number | null
  trendPlan: number | null
  procurementNet: number | null
  demandExtraHigh: number | null
  demandHigh: number | null
  demandLow: number | null
  /** 速報が未着の電圧階級。空 = 確定コマ。非空 = 届いた階級だけで集計した低圧未着の値 (actualStatus とは別軸)。 */
  awaitingVoltageClasses: VoltageClass[]
}

/**
 * サマリーバーの需要ピーク (MAX/MIN)。値・コマ・時刻はすべて backend が
 * 送電端実績から算出したものをそのまま持つ。ピークに添える気温・天気アイコンは含まない —
 * 実績気象 RPC の系列から画面側で合成する (models/summaryTemps.ts)。
 */
export type DetailSummary = {
  maxActual: number
  maxTime: string
  /** 1 始まりのコマ番号 (1..48)。実績気象配列は 0 始まりなので添字は koma-1。 */
  maxKoma: number
  minActual: number
  minTime: string
  /** 1 始まりのコマ番号 (1..48)。 */
  minKoma: number
}

const MISSING_KOMAS = new Set([10, 22])
const INTERPOLATED_KOMAS = new Set([11, 23])

function filterScaleFactor(filter: DetailFilterState): number {
  let s = 1
  if (filter.demandGroup === 'grp_industrial') s *= 0.45
  else if (filter.demandGroup === 'grp_commercial') s *= 0.35
  else if (filter.demandGroup === 'grp_residential') s *= 0.2
  // 電圧階級はフィルターから撤去され、データ量を絞らない (issue #4734)。
  if (filter.ratePlan !== 'all') s *= 0.35
  return s
}

function jitter(i: number, offset: number): number {
  return (((Math.sin((i + offset) * 12.9898) * 43758.5453) % 1) + 1) % 1
}

export function generateDetailData(
  baseScale: number,
  dateSeed: number,
  filter: DetailFilterState,
): DetailKomaData[] {
  const offset = ((dateSeed % 100) + 100) % 100
  const factor = filterScaleFactor(filter)
  const effScale = baseScale * factor
  const data: DetailKomaData[] = []

  for (let i = 0; i < 48; i++) {
    const time = komaIndexToTime(i)
    const baseLoad =
      effScale +
      effScale * 0.12 * Math.sin((i - 6) * 0.14) +
      effScale * 0.07 * Math.sin((i - 12) * 0.08)
    const plan = Math.round(baseLoad + jitter(i, offset) * (effScale * 0.05))

    let actualStatus: ActualStatus = 'actual'
    let actual: number | null = null
    if (i >= GC_KOMA) {
      actualStatus = 'future'
    } else if (MISSING_KOMAS.has(i)) {
      actualStatus = 'missing'
    } else {
      actual = Math.round(
        plan + (jitter(i + 100, offset) - 0.5) * (effScale * 0.05),
      )
      if (INTERPOLATED_KOMAS.has(i)) actualStatus = 'interpolated'
    }

    const corrected =
      actual !== null
        ? Math.round(actual + jitter(i + 200, offset) * (effScale * 0.03))
        : null
    const tempBase = 14 + 8 * Math.sin((i - 8) * 0.13)
    const temp = i < GC_KOMA ? Math.round(tempBase * 10) / 10 : null
    const tempForecast =
      i >= GC_KOMA - 4
        ? Math.round((tempBase + (jitter(i + 400, offset) - 0.5) * 2) * 10) / 10
        : null
    const dewPt =
      Math.round((tempBase - 6 + jitter(i + 500, offset) * 2) * 10) / 10
    const dewPtForecast =
      i >= GC_KOMA - 4
        ? Math.round(
            (tempBase - 6 + (jitter(i + 550, offset) - 0.5) * 2) * 10,
          ) / 10
        : null
    const rainBase = Math.max(
      0,
      1.5 * Math.sin((i - 24) * 0.18) + (jitter(i + 800, offset) - 0.3) * 2,
    )
    const precipitation = i < GC_KOMA ? Math.round(rainBase * 10) / 10 : null
    const trendPlan =
      i >= GC_KOMA
        ? Math.round(plan * (0.97 + jitter(i + 600, offset) * 0.06))
        : null
    const procNet = Math.round(plan * (0.98 + jitter(i + 700, offset) * 0.04))

    let extraHigh: number | null = null
    let high: number | null = null
    let low: number | null = null
    if (actual !== null) {
      extraHigh = Math.round(actual * 0.55)
      high = Math.round(actual * 0.3)
      low = actual - extraHigh - high
    }

    data.push({
      koma: i + 1,
      time,
      demandPlan: plan,
      demandActual: actual,
      actualStatus,
      demandActualCorrected: corrected,
      temperature: temp,
      temperatureForecast: tempForecast,
      dewPoint: dewPt,
      dewPointForecast: dewPtForecast,
      precipitation,
      trendPlan,
      procurementNet: i < GC_KOMA ? procNet : null,
      demandExtraHigh: extraHigh,
      demandHigh: high,
      demandLow: low,
      awaitingVoltageClasses: [],
    })
  }
  return data
}
